import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

const FullArticleView = ({ article, onClose, darkMode = false }) => {
  if (!article) return null;

  // Format date from published_at (already in IST) to "2nd Jan'26" format
  // Parse as UTC to prevent timezone conversion
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse the ISO string and extract components directly to avoid timezone issues
      const dateParts = dateString.split('T')[0].split('-');
      const year = dateParts[0];
      const month = parseInt(dateParts[1]) - 1;
      const day = parseInt(dateParts[2]);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[month];
      const yearShort = year.slice(-2);
      
      // Add ordinal suffix (st, nd, rd, th)
      const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      
      return `${getOrdinal(day)} ${monthName}'${yearShort}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Safe area padding for mobile status bar */}
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      {/* Header */}
      <div className={`sticky top-0 border-b px-4 py-3 flex items-center justify-between ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <Button
          onClick={onClose}
          size="icon"
          variant="ghost"
          className={`h-10 w-10 ${darkMode ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-100'}`}
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Full Article</h2>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Article Content */}
      <div className="px-4 py-4">
        {/* Article Image */}
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-64 object-cover rounded-lg mb-4"
        />

        {/* Source Info and Category */}
        <div className="flex items-center justify-between mb-3">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {article.source} - {formatDate(article.published_at)}
          </p>
          <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium">
            {article.category}
          </span>
        </div>

        {/* Article Title */}
        <h1 className={`text-2xl font-bold mb-4 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {article.title}
        </h1>

        {/* Article Content */}
        <div className="prose max-w-none">
          <p className={`text-base leading-relaxed whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {article.content}
          </p>
        </div>

        {/* Additional spacing at bottom */}
        <div className="h-20" />
      </div>
    </div>
  );
};

export default FullArticleView;
